import {
  Avatar,
  Box,
  Button,
  Card,
  Paper,
  CardActions,
  CardActionArea,
  CardHeader,
  IconButton,
  ListSubheader,
  Divider,
  Typography,
} from '@mui/material';
import {
  RemoveCircle as RemoveCircleIcon,
  Nfc as NfcIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Form } from 'react-final-form';
import _ from 'lodash';
import { useAuth } from '../Auth';
import {
  CREATE_RFID_CARD,
  DELETE_RFID_CARD,
  FETCH_RFID_CARD,
  FETCH_RFID_CARD_SUCCESS,
  UPDATE_RFID_CARD,
  REMOVE_RFID_CARD,
} from '../../actions';
import { ConfirmationDialog } from '../dialogs';
import { StyledField, TextField, SelectField, required } from '../fields';
import { doesIdExist } from '../../apis/utilities';
import { Section } from '../controls';

const { rfidCardTypes, useDallasKeys, useRfidCardCategory } = window.config;

export default function RfidCard() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const rfidCard = useSelector((state) => state.rfidCards.rfidCard, _.isEqual);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [removePerson, setRemovePerson] = useState(null);
  const auth = useAuth();
  const canEdit = auth.isAuthorised('people', true);

  useEffect(() => {
    if (id === 'new') {
      dispatch({
        type: FETCH_RFID_CARD_SUCCESS,
        payload: null,
      });
    } else {
      dispatch({
        type: FETCH_RFID_CARD,
        payload: id,
      });
    }
  }, [id, dispatch]);

  async function onSubmit(values) {
    if (rfidCard) {
      dispatch({
        type: UPDATE_RFID_CARD,
        payload: values,
      });
    } else {
      const exists = await doesIdExist('rfidCards', values.reference);
      if (exists) {
        return { reference: 'Exists' };
      }

      dispatch({
        type: CREATE_RFID_CARD,
        payload: values,
        navigate,
      });
    }
  }

  function handleDelete() {
    if (rfidCard) {
      dispatch({
        type: DELETE_RFID_CARD,
        payload: rfidCard.reference,
      });
    }
  }

  function isDisabled(fieldName) {
    return !canEdit || rfidCard === null;
  }

  const handleRemoveClick = (person) => (event) => {
    event.preventDefault();

    setRemovePerson(person);
  };

  function handleRemove() {
    const body = {
      code: removePerson.code,
      rfidCards: removePerson.rfidCards.filter((card) => card.reference !== id),
    };

    dispatch({
      type: REMOVE_RFID_CARD,
      payload: { id, body },
    });

    setRemovePerson(null);
  }

  return (
    <Form
      initialValues={rfidCard || {}}
      onSubmit={onSubmit}
      render={({
        handleSubmit,
        form: { reset },
        submitting,
        dirty,
        pristine,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <Helmet>
            <title>
              IR3 | {useDallasKeys ? 'Dallas Key' : 'RFID Card'}
              {values.reference ? ` | ${values.reference}` : ''}
            </title>
          </Helmet>
          <Paper sx={{ m: 1, minWidth: 240 }}>
            <CardHeader
              avatar={
                <Avatar aria-label="RFID Card">
                  {useDallasKeys ? <VpnKeyIcon /> : <NfcIcon />}
                </Avatar>
              }
              title={values.reference}
              subheader={values.type}
            />
            <Box>
              <ListSubheader disableSticky>Key Information</ListSubheader>
              <Section>
                <StyledField
                  name="reference"
                  component={TextField}
                  label="Serial Number"
                  validate={required}
                  disabled={!(canEdit && rfidCard === null)}
                />
                {useRfidCardCategory && (
                  <StyledField
                    name="category"
                    component={SelectField}
                    label="Category"
                    values={['Business', 'Personal', 'Commute']}
                    disabled={isDisabled('category')}
                  />
                )}
                <StyledField
                  name="type"
                  component={SelectField}
                  label="Type"
                  values={rfidCardTypes}
                  disabled={isDisabled('type')}
                />
                <StyledField
                  name="label"
                  component={TextField}
                  label="Label"
                  disabled={isDisabled('label')}
                />
              </Section>
              <Divider />
              <ListSubheader disableSticky>Assigned People</ListSubheader>
              <Section>
                {rfidCard && rfidCard.people.length > 0 ? (
                  rfidCard.people.map((person) => (
                    <Card
                      key={person.code}
                      variant="outlined"
                      sx={{ width: 320, mr: 1.5, mb: 1.5 }}
                    >
                      <CardActionArea
                        component={Link}
                        to={`/resources/people/${encodeURIComponent(
                          person.code
                        )}`}
                      >
                        <CardHeader
                          avatar={
                            <Avatar src={person.picture}>
                              <PersonIcon />
                            </Avatar>
                          }
                          action={
                            <IconButton
                              title="Remove"
                              onClick={handleRemoveClick(person)}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <RemoveCircleIcon />
                            </IconButton>
                          }
                          title={`${person.forenames} ${person.surname}`}
                          subheader={person.collarNumber}
                        />
                      </CardActionArea>
                    </Card>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    There is no one assigned to this card
                  </Typography>
                )}
              </Section>
            </Box>
            <CardActions>
              <Button
                color="primary"
                type="submit"
                disabled={pristine || submitting}
              >
                Save
              </Button>
              <Button
                color="primary"
                disabled={pristine || submitting}
                onClick={reset}
              >
                Cancel
              </Button>
              <Button
                color="error"
                onClick={() => setDeleteOpen(true)}
                disabled={rfidCard === null}
              >
                Delete
              </Button>
            </CardActions>
          </Paper>
          <ConfirmationDialog
            action="Delete"
            open={deleteOpen}
            itemId={values.reference}
            onOk={handleDelete}
            onCancel={() => setDeleteOpen(false)}
          />

          <ConfirmationDialog
            action="Remove"
            open={!!removePerson}
            itemId={
              removePerson
                ? `${removePerson.forenames || ''} ${
                    removePerson.surname || ''
                  }` ||
                  removePerson.collarNumber ||
                  removePerson.code
                : ''
            }
            onOk={handleRemove}
            onCancel={() => setRemovePerson(null)}
          />
        </form>
      )}
    />
  );
}
